import type {
  ETokenUsageTrendMetric,
  IGetTokenUsageByModelResponse,
  ITokenCostModelGroup,
  ITokenUsageModelCost,
  TTokenUsageExportFormat,
} from "./token-usage";

// NOTE: The period presets the Token & Cost Usage screen offers. Shared rather
// than owned by one client: web and mobile must resolve the same preset to the
// same bounds, or the two apps report different spend for the same "Last 30
// days".
export enum ETokenCostPeriod {
  LAST_7_DAYS = "last_7_days",
  LAST_30_DAYS = "last_30_days",
  CURRENT_MONTH = "current_month",
  ALL_TIME = "all_time",
}

export interface IDateRange {
  dateFrom?: string;
  dateTo?: string;
}

export interface IFormatCurrencyProps {
  value: number | null;
  currency: string;
}

// NOTE: NSAI-5207 — the two counts that decide whether a figure of 0 is an
// absence of usage or an absence of data. Structurally satisfied by both
// ITokenUsageModelCost and the totals/subtotal shape, which is what lets one
// implementation serve the row, the subtotal and the footer.
export interface IAreAllTokenCountsUnknownProps {
  records: number;
  recordsMissingTokens: number;
}

// NOTE: NSAI-5210 — takes the contributing MODELS, never the three aggregate
// figures. On a subtotal or the totals row those come from different models, so
// one model's cached tokens and another's input cost would satisfy the row-level
// test together and the cell would claim tokens were charged that were not.
export interface IIsCachedChargedAtInputRateProps {
  models: ITokenUsageModelCost[];
}

export interface IFormatTokensOrUnknownProps {
  value: number;
  // NOTE: Every record behind this figure lacked token counts, so the 0 is an
  // absence of data rather than an absence of usage.
  areAllTokenCountsUnknown: boolean;
}

// NOTE: Which of the three reasons a cost cell is blank, paired with the copy
// that says so. Resolved in one place so a test id and its explanation can never
// disagree about what the reader is being told.
// NOTE: Literal keys, not `string` — the settings namespace is typed, so a
// widened key would force a cast at the t() call and take the compiler's check
// on these three strings away with it.
export type TDashTitleKey =
  | "tokenCostUsage.chargedAtInputRateCellTitle"
  | "tokenCostUsage.unpricedCellTitle"
  | "tokenCostUsage.unreportedUsageCellTitle";

export interface IDashCause {
  testId: string;
  titleKey: TDashTitleKey;
}

export interface IResolveDashCauseProps {
  areAllTokenCountsUnknown: boolean;
  // NOTE: NSAI-5210 — only ever true for a cached-input cost cell; every other
  // dimension has no "charged somewhere else" state to report.
  isChargedAtInputRate: boolean;
}

export interface IResolveTokenCostFigureNotesProps {
  report: IGetTokenUsageByModelResponse;
  // NOTE: NSAI-6266 — whether this report is scoped to an ORGANIZATION. Only an
  // organization has an enforced budget for the report to disagree with, so it
  // decides one note that no property of `report` can answer. Required rather
  // than defaulted: a silent default would pick a behaviour for every future
  // caller, and the two answers point a reader at different screens.
  isOrganizationScope: boolean;
}

export interface IFormatTrendAxisValueProps {
  value: number;
  metric: ETokenUsageTrendMetric;
  currency: string;
  // NOTE: NSAI-5214 — the top of the plotted domain. Precision is chosen once
  // from it so every tick on one axis is formatted alike; formatting each tick
  // from its own magnitude would stack "$0.000023" under "$0.01".
  maxValue: number;
}

export interface IBuildExportFileNameProps {
  scopeLabel: string;
  format: TTokenUsageExportFormat;
  allTimeLabel: string;
  dateFrom?: string;
  dateTo?: string;
}

// NOTE: NSAI-5153 — the identity a per-model row is keyed on. `groupKey` and
// `modelIndex` are the tiebreaker of last resort, reachable only when the row
// carries no identity at all.
export interface IBuildTokenCostRowKeyProps {
  model: ITokenUsageModelCost;
  groupKey: string;
  modelIndex: number;
}

// NOTE: NSAI-5167 — a page of the per-model breakdown, measured in GROUPS rather
// than in rows. A display name can occupy several catalog rows (one per region +
// hosting provider) and NSAI-5211 made those contiguous under one subtotal;
// slicing rows would cut a group in half and leave a subtotal describing rows the
// reader cannot see. So "rows per page" means "models per page" — the unit the
// reader counts in on both platforms.
export interface IResolveTokenCostPageProps {
  groups: ITokenCostModelGroup[];
  // NOTE: The requested page, which may be past the end: a refetch can shorten
  // the model list with no interaction to reset the page against.
  page: number;
  rowsPerPage: number;
}

export interface ITokenCostPage {
  // NOTE: The page actually shown, clamped into range. Fed back to the paging
  // control so it can never point at a page the list is not rendering.
  currentPage: number;
  visibleGroups: ITokenCostModelGroup[];
}
